/**
 * Parwaaz — English strings. The canonical dictionary: `hi.ts` is typed against this file's
 * keys so the two can never drift apart silently.
 */
import type { Dict } from './index';

export const en: Dict = {
  // ── Navigation ─────────────────────────────────────────────────────────
  'nav.home': 'Home',
  'nav.register': 'Register',
  'nav.status': 'Check status',
  'nav.verify': 'Verify a card',
  'nav.office': 'Office',
  'nav.stats': 'Statistics',
  'nav.menu': 'Menu',
  'nav.about': 'About',
  'nav.staffSignIn': 'Staff sign in',
  'nav.primaryAria': 'Primary',
  'nav.footerAria': 'Footer',
  'nav.skipToContent': 'Skip to content',
  'footer.privacyNotice': 'Privacy notice',

  // ── Form steps ─────────────────────────────────────────────────────────
  'steps.who.title': 'Who you are',
  'steps.who.help': 'Your personal and contact details',
  'steps.photo.title': 'Your photo',
  'steps.photo.help': 'A clear, recent photo for your Free Flyer Card',
  'steps.documents.title': 'Your documents',
  'steps.documents.help': "Passport or Indian ID, and visa details if you're a foreign national",
  'steps.equipment.title': 'Your equipment',
  'steps.equipment.help': 'Glider, reserve parachute and certification details',
  'steps.safety.title': 'Safety and emergency',
  'steps.safety.help': 'Insurance, medical fitness and an emergency contact',
  'steps.declaration.title': 'Declaration and bond',
  'steps.declaration.help': 'Read and accept the declaration and indemnity bond',

  // ── Field labels (one per PilotFields key) ───────────────────────────────
  'field.givenNames': 'Given name(s)',
  'field.surname': 'Surname',
  'field.fathersName': "Father's name",
  'field.addressLine': 'Address',
  'field.city': 'City / Town',
  'field.region': 'State / Region',
  'field.country': 'Country of residence',
  'field.postcode': 'Postal code',
  'field.nationality': 'Nationality',
  'field.dob': 'Date of birth',
  'field.phone': 'Phone number',
  'field.email': 'Email address',
  'field.docType': 'Identity document type',
  'field.docNumber': 'Document number',
  'field.docIssuingCountry': 'Issuing country',
  'field.docExpiry': 'Document expiry date',
  'field.mrzLine1': 'MRZ line 1',
  'field.mrzLine2': 'MRZ line 2',
  'field.isForeign': 'Foreign national',
  'field.arrivalDate': 'Date of arrival in India',
  'field.visaType': 'Visa type',
  'field.visaValidFrom': 'Visa valid from',
  'field.visaValidTo': 'Visa valid to',
  'field.certBody': 'Certifying body',
  'field.certLevel': 'Certification level',
  'field.certNumber': 'Certificate number',
  'field.certIssued': 'Certificate issue date',
  'field.certExpiry': 'Certificate expiry date',
  'field.certHolderName': 'Name on certificate',
  'field.gliderMake': 'Glider make',
  'field.gliderModel': 'Glider model',
  'field.gliderSize': 'Glider size',
  'field.gliderClass': 'Glider class (EN rating)',
  'field.reserveMake': 'Reserve parachute make',
  'field.reserveModel': 'Reserve parachute model',
  'field.reserveRepack': 'Reserve last repacked',
  'field.insProvider': 'Insurance provider',
  'field.insPolicyNo': 'Insurance policy number',
  'field.insInsuredName': 'Name on insurance policy',
  'field.insCoverText': 'Insurance cover details',
  'field.insValidTo': 'Insurance valid to',
  'field.medicalValidTo': 'Medical certificate valid to',
  'field.emgName': 'Emergency contact name',
  'field.emgRelationship': 'Emergency contact relationship',
  'field.emgPhone': 'Emergency contact phone',
  'field.bloodGroup': 'Blood group',
  'field.declarationAccepted': 'Declarations',
  'field.bondAccepted': 'Indemnity bond',
  'field.typedName': 'Type your full name to sign',
  'field.consentPurpose': 'I consent to my data being used as described above',

  // ── Declaration clauses (verbatim from the paper form) ───────────────────
  'declaration.clause1': 'I am in good physical and mental health.',
  'declaration.clause2': 'I understand the nature of the sport and risk involved with flying paragliders in Himalayan conditions.',
  'declaration.clause3':
    'I choose to fly at my own risk and release the Himachal Pradesh Government administration and its appointed committees and assisting local bodies of any liability.',
  'declaration.clause4':
    'I further understand and declare that in case of any accident and search & rescue and medical treatment, it would be at my own cost.',

  // ── Indemnity bond clauses I-VII (faithful paraphrase) ────────────────────
  'bond.clause1':
    'I have been informed of the dangers of paragliding, including flying over remote places without medical facilities, the forces of nature, extreme weather, physical exertion and the difficulties of evacuation.',
  'bond.clause2':
    'I release, waive and indemnify the Government of Himachal Pradesh, and agree not to sue it, for any loss, injury or death, including any arising from negligence or failure to rescue, evacuate or treat me.',
  'bond.clause3': 'I understand that the State Government will not bear any expenditure incurred in my rescue or medical care.',
  'bond.clause4':
    'I acknowledge that adventure travel can cause serious bodily injury, property damage and death, that this waiver is as broad as the law allows, and that I am entering into it freely.',
  'bond.clause5': 'I will not have liquor or illegal drugs in my possession while participating in this activity.',
  'bond.clause6': 'I have read and understood all the terms above and agree to them of my own free will.',
  'bond.clause7': 'I will not fly over the residence of His Holiness the Dalai Lama at McLeodganj (Upper Dharamshala).',

  // ── Application status (pilot-friendly wording) ──────────────────────────
  'status.Submitted': 'Received — waiting for review',
  'status.Validated': 'Checks complete — waiting to be picked up for evaluation',
  'status.CorrectionRequested': 'Action needed — please provide the requested corrections',
  'status.ReadyForEvaluation': 'In queue for evaluation',
  'status.Evaluated': 'Evaluated — awaiting recommendation',
  'status.EvaluationFailed': 'Evaluation did not pass — under office review',
  'status.UnderReview': 'Under review by the SDM office',
  'status.Approved': 'Approved — your card is being issued',
  'status.CardIssued': 'Your Free Flyer Card has been issued',
  'status.Rejected': 'Application not approved',
  'status.Revoked': 'Card revoked',
  'status.Expired': 'Card expired',
  'status.greeting': 'Hello, {name}',

  // ── Verdict strings (mirror card.ts Verdict titles) ──────────────────────
  'verdict.valid': 'Valid',
  'verdict.validExpiresToday': 'Valid — expires today',
  'verdict.staleList': 'Valid — revocation list is {hours} h old',
  'verdict.notYetValid': 'Not yet valid',
  'verdict.expired': 'Expired',
  'verdict.revoked': 'REVOKED',
  'verdict.signatureInvalid': 'Signature invalid — card altered or forged',
  'verdict.unknownKey': 'Unknown signing key',
  'verdict.invalidCard': 'Invalid card',

  // ── Common buttons ─────────────────────────────────────────────────────
  'button.next': 'Next',
  'button.back': 'Back',
  'button.submit': 'Submit',
  'button.retry': 'Retry',
  'button.save': 'Save',
  'button.continueOffline': 'Continue offline',
  'button.confirm': 'Confirm',
  'button.cancel': 'Cancel',
  'button.close': 'Close',
  'button.download': 'Download',
  'button.print': 'Print',
  'button.upload': 'Upload',
  'button.edit': 'Edit',
  'button.delete': 'Delete',

  // ── Errors ─────────────────────────────────────────────────────────────
  'error.network': 'Network error — please check your connection and try again.',
  'error.validation': 'Please fix the highlighted fields.',
  'error.unauthorized': 'Please log in again.',
  'error.forbidden': "You don't have permission to do that.",
  'error.notFound': 'Not found.',
  'error.conflict': 'This record changed elsewhere — please reload and try again.',
  'error.rateLimited': 'Too many requests — please wait a moment and try again.',
  'error.internal': 'Something went wrong on our end. Please try again.',
  'error.tryAgain': 'Try again',

  // ── DPDP purpose/consent statement ────────────────────────────────────────
  'consent.purpose':
    'Your information is collected only to register you as a pilot and issue the Free Flyer Card under the authority of the Sub-Divisional Magistrate. It is retained for {years} years and can be corrected or queried by contacting the grievance officer.',

  // ── Field hints — why the office needs each one ───────────────────────────
  'hint.givenNames': 'Exactly as printed on your identity document.',
  'hint.surname': 'Exactly as printed on your identity document.',
  'hint.fathersName': "Used on Indian documents and the register to confirm your identity.",
  'hint.addressLine': 'Your permanent address, for the register and in case we need to reach you.',
  'hint.city': 'City or town of your permanent address.',
  'hint.region': 'State or region of your permanent address.',
  'hint.country': 'Country of your permanent address.',
  'hint.postcode': 'Postal code of your permanent address.',
  'hint.nationality': 'Determines whether visa fields are needed and how your name is checked.',
  'hint.dob': 'Checked for a plausible flying age and against your document.',
  'hint.phone': "So the office can reach you about your application. Include your country code.",
  'hint.email': "Optional, but you'll get your status link and acknowledgement by email.",
  'hint.docType': 'Foreign nationals must use a passport; Indian nationals may use any listed ID.',
  'hint.docNumber': 'As printed on your document — checked against the MRZ if you scan it.',
  'hint.docIssuingCountry': 'The country that issued your document.',
  'hint.docExpiry': 'Must stay valid through the end of the card period.',
  'hint.isForeign': 'Foreign nationals answer a few extra questions about their visa.',
  'hint.arrivalDate': 'Used to confirm your visa covers your stay.',
  'hint.visaType': 'The kind of visa you entered India on.',
  'hint.visaValidFrom': 'Must cover the whole card period.',
  'hint.visaValidTo': 'Must cover the whole card period.',
  'hint.certBody': 'Your paragliding certifying organisation.',
  'hint.certLevel': 'Determines the maximum glider class typically allowed.',
  'hint.certNumber': 'As printed on your certificate.',
  'hint.certIssued': 'Date your certification was issued.',
  'hint.certExpiry': 'Must not have expired.',
  'hint.certHolderName': 'Checked against your name — must be a close match.',
  'hint.gliderMake': 'The wing you plan to fly at Bir Billing.',
  'hint.gliderModel': 'The wing you plan to fly at Bir Billing.',
  'hint.gliderSize': 'Size/model designation, e.g. "23" or "M".',
  'hint.gliderClass': 'EN or DHV safety class — checked against your certification level.',
  'hint.reserveMake': 'Your reserve parachute, for the safety record.',
  'hint.reserveModel': 'Your reserve parachute, for the safety record.',
  'hint.reserveRepack': 'Reserves should be repacked periodically — helps us confirm it is airworthy.',
  'hint.insProvider': 'Your travel/adventure-sport insurance provider.',
  'hint.insPolicyNo': 'As printed on your policy.',
  'hint.insInsuredName': 'Checked against your name.',
  'hint.insCoverText': 'Paste or type the line of your policy that mentions paragliding — we check it covers the sport.',
  'hint.insValidTo': 'Must cover the whole card period.',
  'hint.medicalValidTo': 'If you carry separate medical/life cover, its expiry date.',
  'hint.emgName': "Someone we can contact if there's an emergency while you're flying.",
  'hint.emgRelationship': 'How this person is related to you.',
  'hint.emgPhone': 'Must be different from your own number.',
  'hint.bloodGroup': 'Optional — shared only with rescue teams if needed.',
  'hint.typedName': 'Type your full name exactly as given above — this is your signature.',
  'hint.consentPurpose': 'Required to submit your application.',

  // ── MRZ capture panel ──────────────────────────────────────────────────────
  'mrz.capture.title': 'Scan your passport',
  'mrz.capture.instructions': 'Place the two lines at the bottom of the passport photo page inside the frame, in good light.',
  'mrz.capture.button': 'Scan passport photo page',
  'mrz.capture.retake': 'Retake',
  'mrz.capture.scanning': 'Reading the passport…',
  'mrz.capture.skip': "I'll type the MRZ lines myself",
  'mrz.capture.typeInstead': 'Type the two MRZ lines from the bottom of your passport photo page.',
  'mrz.capture.line1': 'MRZ line 1',
  'mrz.capture.line2': 'MRZ line 2',
  'mrz.capture.locked': 'Filled in from your passport — fields are locked to avoid mismatches.',
  'mrz.capture.unlock': 'Edit anyway',
  'mrz.check.docNo': 'Document number',
  'mrz.check.dob': 'Date of birth',
  'mrz.check.expiry': 'Expiry date',
  'mrz.check.composite': 'Overall check digit',
  'mrz.check.pending': 'Not checked yet',
  'mrz.check.ok': 'Matches',
  'mrz.check.bad': "Doesn't match",

  // ── Photo capture ──────────────────────────────────────────────────────────
  'photo.capture.title': 'Take your card photo',
  'photo.capture.guide': 'Face the camera directly, plain background, head and shoulders inside the guide.',
  'photo.capture.button': 'Take or choose a photo',
  'photo.capture.retake': 'Retake',
  'photo.capture.zoom': 'Zoom',
  'photo.capture.confirm': 'Use this photo',
  'photo.capture.processing': 'Processing photo…',
  'ins.cover.scan': 'Scan policy document to read cover text',

  // ── File upload field labels (distinct from same-topic text fields) ──────────
  'upload.docLabel': 'Identity document photo',
  'upload.certLabel': 'Certificate photo',
  'upload.insLabel': 'Insurance document',
  'upload.photoLabel': 'Card photo',

  // ── Document type / visa type options ───────────────────────────────────────
  'doc.type.passport': 'Passport',
  'doc.type.aadhaar': 'Aadhaar',
  'doc.type.voter_id': 'Voter ID',
  'doc.type.driving_licence': 'Driving licence',
  'doc.type.other': 'Other',
  'visa.type.eVisa': 'e-Visa',
  'visa.type.tourist': 'Tourist visa',
  'visa.type.other': 'Other',

  // ── Wizard chrome ─────────────────────────────────────────────────────────
  'wizard.stepOf': 'Step {current} of {total}',
  'wizard.resume.title': 'Resume your application?',
  'wizard.resume.body': "We found an application you started earlier on this device. Pick up where you left off, or start a fresh one.",
  'wizard.resume.resume': 'Resume',
  'wizard.resume.startOver': 'Start fresh',
  'wizard.addPhoto': 'Add a file',
  'wizard.uploadedFile': 'Uploaded: {name}',
  'wizard.requiredFile': 'This document is required.',
  'wizard.gliderSuggestions': 'Common wings',
  'wizard.otherOption': 'Other',

  // ── Review step ──────────────────────────────────────────────────────────
  'review.title': 'Review your application',
  'review.edit': 'Edit',
  'review.findingsTitle': 'Preliminary checks — the office re-runs these',
  'review.amberNotice': 'You can submit now, but the office will ask you to fix:',
  'review.readAll': 'I have read all the clauses above',
  'review.scrollHint': 'Scroll to the end to continue.',

  // ── Submission ─────────────────────────────────────────────────────────────
  'submit.offline.title': 'Saved — will send when you\'re back online',
  'submit.offline.body': "You're offline. Your application is saved on this device and will be sent automatically as soon as you have a connection.",
  'submit.sending': 'Sending your application…',
  'submit.success.title': 'Application received',
  'submit.success.reference': 'Your reference',
  'submit.success.body': 'Save this reference and the link below — you\'ll need them to check your status.',
  'submit.success.copyLink': 'Copy status link',
  'submit.success.copied': 'Copied',
  'submit.success.shareLink': 'Share',
  'submit.success.downloadPdf': 'Download acknowledgement (PDF)',
  'submit.success.viewStatus': 'View my status',
  'submit.success.whatNext.title': 'What happens next',
  'submit.success.whatNext.1': 'The office checks your documents and details.',
  'submit.success.whatNext.2': 'An evaluator reviews your certification and equipment.',
  'submit.success.whatNext.3': 'The SDM approves and your Free Flyer Card is issued.',

  // ── Status page ─────────────────────────────────────────────────────────
  'status.page.title': 'Application {ref}',
  'status.page.checkTitle': 'Check your application status',
  'status.notFound.title': 'Application not found',
  'status.notFound.body': "We couldn't find that application with this link. Check the reference and link and try again.",
  'status.offline.title': "You're offline",
  'status.offline.body': 'Showing the last known status from {at}.',
  'status.findings.title': 'Findings summary',
  'status.correction.title': 'Action needed',
  'status.correction.body': 'The office has asked for the following before your application can proceed:',
  'status.correction.submit': 'Submit correction',
  'status.correction.sent': 'Correction sent — thank you.',
  'status.card.title': 'Your Free Flyer Card',
  'status.card.showQr': 'Show QR full screen',
  'status.card.brightnessHint': 'Turn your screen brightness all the way up before scanning.',
  'status.card.download': 'Download card (PDF)',
  'status.card.addHome': 'Add this page to your home screen for offline access at the launch site.',
  'status.card.revoked': 'This card has been revoked and is no longer valid.',
  'status.myApplications': 'Your saved applications',

  // ── Timeline state explanations ───────────────────────────────────────────
  'status.explain.Submitted': 'We have received your application and are running initial checks.',
  'status.explain.Validated': 'Your checks are complete and your application is queued for evaluation.',
  'status.explain.CorrectionRequested': 'Please provide the corrections requested below.',
  'status.explain.ReadyForEvaluation': 'Your application is in the queue to be evaluated by our team.',
  'status.explain.Evaluated': 'Your certification and equipment have been evaluated; awaiting a recommendation.',
  'status.explain.EvaluationFailed': 'Evaluation did not pass — the SDM office is reviewing your application.',
  'status.explain.UnderReview': 'The SDM office is reviewing your application.',
  'status.explain.Approved': 'Your application is approved — your card is being issued.',
  'status.explain.CardIssued': 'Your Free Flyer Card has been issued. It is shown below.',
  'status.explain.Rejected': 'Your application was not approved.',
  'status.explain.Revoked': 'Your card has been revoked.',
  'status.explain.Expired': 'Your card has expired for this season.',

  // ── Office console (WP7) — chrome, guards, shortcuts ──────────────────────
  'office.nav.overview': 'Overview',
  'office.nav.queue': 'Queue',
  'office.nav.cases': 'Cases',
  'office.nav.dashboard': 'Dashboard',
  'office.nav.issue': 'Issue cards',
  'office.nav.staff': 'Staff',
  'office.nav.settings': 'Settings',
  'office.nav.exports': 'Exports',
  'office.nav.responder': 'Responder',
  'office.header.signOut': 'Sign out',
  'office.guard.signInTitle': 'Sign in required',
  'office.guard.signInBody': 'The office console is for SDM office staff. Sign in with your staff passphrase to continue.',
  'office.guard.goToSignIn': 'Go to staff sign in',
  'office.guard.notAllowedTitle': 'Not available for your role',
  'office.guard.notAllowedBody': "Your role ({role}) doesn't have access to this section.",
  'office.guard.sessionExpired': 'Your session expired — please sign in again.',
  'office.help.title': 'Keyboard shortcuts',
  'office.help.search': 'Focus search',
  'office.help.moveRows': 'Move between rows',
  'office.help.openRow': 'Open selected row',
  'office.help.closeModal': 'Close dialog / modal',
  'office.help.open': 'Show this help',

  // ── Office overview (/office) — the SDM/DC executive snapshot ────────────
  'office.overview.greeting.morning': 'Good morning, {name}',
  'office.overview.greeting.afternoon': 'Good afternoon, {name}',
  'office.overview.greeting.evening': 'Good evening, {name}',
  'office.overview.season.daysLeft': '{days} days left',
  'office.overview.season.lastDay': 'Last day',
  'office.overview.season.ended': 'Season ended',
  'office.overview.status.registerLive': 'Register live',
  'office.overview.status.backend': 'Backend: {backend} · online',
  'office.overview.status.backend.offline': 'Backend: offline',
  'office.overview.backend.appsscript': 'Apps Script',
  'office.overview.backend.devserver': 'Dev server',
  'office.overview.backend.demo': 'Demo',
  'office.overview.kpi.pending': 'Pending review',
  'office.overview.kpi.correction': 'Awaiting correction',
  'office.overview.kpi.correction.oldest': 'oldest {age}',
  'office.overview.kpi.ready': 'Ready to issue',
  'office.overview.kpi.issuedToday': 'Issued today',
  'office.overview.kpi.expiring': 'Expiring in 7 days',
  'office.overview.kpi.avgApproval': 'Average approval time',
  'office.overview.kpi.avgApproval.value': '{hours}h',
  'office.overview.kpi.avgApproval.none': 'No data yet',
  'office.overview.pipeline.title': 'Pipeline',
  'office.overview.pipeline.stage.submitted': 'Submitted',
  'office.overview.pipeline.stage.checked': 'Checked',
  'office.overview.pipeline.stage.evaluation': 'Evaluation',
  'office.overview.pipeline.stage.review': 'Review',
  'office.overview.pipeline.stage.approved': 'Approved',
  'office.overview.pipeline.stage.issued': 'Issued',
  'office.overview.decision.title': 'Needs your decision',
  'office.overview.decision.andMore': 'and {count} more',
  'office.overview.decision.review': 'Review',
  'office.overview.decision.view': 'View',
  'office.overview.decision.empty.title': 'Nothing waiting on you',
  'office.overview.decision.empty.body': "There's nothing in your queue right now — check back later, or browse the full queue.",
  'office.overview.decision.assignedTo': 'Assigned to {name}',
  'office.overview.decision.unassigned': 'Unassigned',
  'office.overview.season.title': 'Season at a glance',
  'office.overview.season.registrationsByDay': 'Registrations, last 30 days',
  'office.overview.season.nationalitySplit': 'Indian vs. foreign',
  'office.overview.season.topNationalities': 'Top nationalities',
  'office.overview.season.tierMix': 'Risk tier mix',
  'office.overview.season.noData': 'No registrations yet this season.',
  'office.overview.activity.title': 'Activity',
  'office.overview.activity.empty': 'No activity yet this season.',
  'office.overview.activity.line': '{name} → {state}',
  'office.overview.quickActions.title': 'Quick actions',
  'office.overview.quickActions.registerOnBehalf': 'Register a pilot on their behalf',
  'office.overview.quickActions.issueCards': 'Issue cards',
  'office.overview.quickActions.verify': 'Verify a card',
  'office.overview.quickActions.printSheet': 'Print sheet',
  'office.overview.quickActions.publicStats': 'Public statistics',
  'office.overview.offline.body': 'Showing the last saved numbers from {time}.',
  'office.overview.error.title': 'Could not load the overview',
  'office.overview.error.retry': 'Try again',

  // ── Queue ──────────────────────────────────────────────────────────────
  'queue.title': 'Queue',
  'queue.description': 'Applications waiting on an office action, grouped by state.',
  'queue.view.mine': 'My queue',
  'queue.view.all': 'All',
  'queue.view.attention': 'Needs attention',
  'queue.view.ready': 'Ready to issue',
  'queue.view.issued': 'Issued',
  'queue.filters.state': 'State',
  'queue.filters.tier': 'Risk tier',
  'queue.filters.nationality': 'Nationality',
  'queue.filters.indian': 'Indian',
  'queue.filters.foreign': 'Foreign',
  'queue.filters.assignedToMe': 'Assigned to me',
  'queue.filters.search': 'Search',
  'queue.filters.searchPlaceholder': 'Search name, ref, document, card…',
  'queue.table.age': 'Age',
  'queue.table.name': 'Name',
  'queue.table.nationality': 'Nationality',
  'queue.table.cert': 'Cert.',
  'queue.table.glider': 'Glider class',
  'queue.table.certGlider': 'Cert. / Class',
  'queue.table.tier': 'Findings',
  'queue.table.assigned': 'Assigned',
  'queue.table.state': 'State',
  'queue.bulk.selected': '{count} selected',
  'queue.bulk.recommendAll': 'Recommend all',
  'queue.bulk.approveAll': 'Approve all',
  'queue.bulk.running': 'Working on {done} of {total}…',
  'queue.bulk.done': 'Done: {ok} succeeded, {failed} failed.',
  'queue.refresh': 'Refresh',
  'queue.lastUpdated': 'Last updated {time}',
  'queue.empty.title': 'Nothing here',
  'queue.empty.body': 'No applications match this view and filter combination.',
  'queue.unassigned': 'Unassigned',

  // ── Case view ──────────────────────────────────────────────────────────
  'case.tabs.entries': 'Entries',
  'case.tabs.checks': 'Checks',
  'case.tabs.documents': 'Documents',
  'case.back': 'Back to queue',
  'case.notFound.title': 'Case not found',
  'case.notFound.body': "This application reference doesn't exist or you don't have access to it.",
  'case.copy': 'Copy',
  'case.copied': 'Copied',
  'case.whatsapp': 'WhatsApp',
  'case.email': 'Email',
  'case.riskTier': 'Risk tier',
  'case.rulesetVersion': 'Ruleset version',
  'case.section.identity': "Pilot's details",
  'case.section.documents': 'Identity document',
  'case.section.equipment': 'Equipment and certification',
  'case.section.safety': 'Safety, insurance and emergency contact',
  'case.override': 'Override',
  'case.override.title': 'Override finding',
  'case.override.reasonLabel': 'Reason for overriding this finding',
  'case.override.reasonHint': 'Recorded on the audit trail against your name.',
  'case.documents.doc': 'Identity document',
  'case.documents.photo': 'Card photo',
  'case.documents.cert': 'Certification card',
  'case.documents.ins': 'Insurance',
  'case.documents.none': 'Not uploaded',
  'case.documents.open': 'Open',
  'case.documents.zoomIn': 'Zoom in',
  'case.documents.zoomOut': 'Zoom out',
  'case.documents.rotate': 'Rotate',
  'case.documents.typedVsDocument': 'Typed vs. document',
  'case.documents.typed': 'Typed by pilot',
  'case.documents.mrz': 'From document (MRZ)',
  'case.timeline.title': 'Timeline & audit',
  'case.timeline.empty': 'No audit history yet.',
  'case.notes.title': 'Notes',
  'case.notes.placeholder': 'Add an internal note…',
  'case.notes.add': 'Add note',
  'case.notes.empty': 'No notes yet.',
  'case.conflict.title': 'This case changed',
  'case.conflict.body': 'Someone else updated this case while you were looking at it.',
  'case.conflict.reload': 'Reload',
  'case.print': 'Print case summary',
  'case.reload': 'Reload',
  'case.actionBar.empty': 'No actions available for your role on this case right now.',

  // ── Case actions (action bar + modals) ────────────────────────────────
  'action.assign.label': 'Assign',
  'action.assign.title': 'Assign this case',
  'action.assign.staffLabel': 'Assign to',
  'action.assign.staffTextLabel': 'Staff username',
  'action.note.label': 'Add note',
  'action.note.title': 'Add an internal note',
  'action.note.textLabel': 'Note',
  'action.requestCorrection.label': 'Request correction',
  'action.requestCorrection.title': 'Request a correction',
  'action.requestCorrection.itemsLabel': 'What needs fixing',
  'action.requestCorrection.messageLabel': 'Message to the pilot',
  'action.requestCorrection.messageHint': 'Optional — shown alongside the checklist on their status page.',
  'action.evaluate.label': 'Evaluate',
  'action.evaluate.title': 'Evaluation checklist',
  'action.evaluate.outcomeLabel': 'Outcome',
  'action.evaluate.outcome.pass': 'Pass',
  'action.evaluate.outcome.conditional': 'Conditional',
  'action.evaluate.outcome.fail': 'Fail',
  'action.evaluate.notesLabel': 'Notes',
  'action.evaluate.checklist.identity': 'Pilot identity matches document',
  'action.evaluate.checklist.certSighted': 'Certification card sighted',
  'action.evaluate.checklist.gliderMatch': 'Glider matches declared model and class',
  'action.evaluate.checklist.reserve': 'Reserve fitted and in date',
  'action.evaluate.checklist.gear': 'Harness and helmet acceptable',
  'action.evaluate.checklist.briefed': 'Pilot briefed on site rules and no-fly zones',
  'action.evaluate.checklist.fitToday': 'Weather/skill fit for today',
  'action.evaluate.checklist.insurance': 'Insurance sighted',
  'action.recommend.label': 'Recommend',
  'action.recommend.title': 'Send a recommendation',
  'action.recommend.recommendationLabel': 'Recommendation',
  'action.recommend.recommendation.approve': 'Recommend approval',
  'action.recommend.recommendation.reject': 'Recommend rejection',
  'action.recommend.noteLabel': 'Note',
  'action.approve.label': 'Approve',
  'action.approve.title': 'Approve this application',
  'action.approve.reasonLabel': 'Reason (optional)',
  'action.approve.warnNotice': 'This application still has unresolved warnings — you can approve anyway.',
  'action.reject.label': 'Reject',
  'action.reject.title': 'Reject this application',
  'action.reject.reasonLabel': 'Reason',
  'action.reject.reasonHint': 'At least 10 characters — shown to the pilot.',
  'action.revoke.label': 'Revoke card',
  'action.revoke.title': 'Revoke this card',
  'action.revoke.reasonLabel': 'Reason',
  'action.reissue.label': 'Reissue',
  'action.reissue.title': 'Reissue a card',
  'action.reissue.confirm': 'A new card will be allocated for this pilot from /issue. Continue?',
  'action.markPrinted.label': 'Mark printed',
  'action.markPrinted.title': 'Mark card as printed',
  'action.markPrinted.sheetLabel': 'Print sheet / batch id',
  'action.reopen.label': 'Reopen',
  'action.reopen.title': 'Reopen this application',
  'action.reopen.confirm': 'This sends the application back to the start of the queue. Continue?',
  'action.override.label': 'Override',
  'action.reasonLabel': 'Reason',
  'action.submit': 'Submit',
  'action.success': '{action} done for {ref}.',

  // ── WhatsApp / email templates ─────────────────────────────────────────
  'template.correction.text': 'Hello {name}, this is the SDM Baijnath paragliding office regarding your Free Flyer Card application {ref}. We need a correction from you — please check your status page for details.',
  'template.approval.text': 'Hello {name}, good news — your Free Flyer Card application {ref} has been approved. Your card will be issued shortly.',
  'template.cardReady.text': 'Hello {name}, your Free Flyer Card for application {ref} is ready. Please visit the SDM office at Bir Billing to collect it.',

  // ── Evaluator screen ───────────────────────────────────────────────────
  'evaluator.title': "Today's evaluations",
  'evaluator.description': 'Applicants ready for a glider/certification check at the launch site.',
  'evaluator.search': 'Search name, ref or card number',
  'evaluator.scanCard': 'Scan card',
  'evaluator.empty.title': 'Nothing to evaluate right now',
  'evaluator.empty.body': 'New applicants will appear here as soon as they clear initial checks.',
  'evaluator.classWarning': 'Glider class exceeds this certification level',
  'evaluator.open': 'Open',
  'evaluator.back': 'Back to list',
  'evaluator.checklist.title': 'Assessment checklist',
  'evaluator.signature.title': 'Evaluator signature',
  'evaluator.signature.clear': 'Clear',
  'evaluator.signature.hint': 'Sign with your finger or stylus. Kept on this device only; a short hash is recorded with your notes as sig:<hash>.',
  'evaluator.signature.required': 'Please sign before submitting.',
  'evaluator.submit': 'Submit assessment',
  'evaluator.submitted': 'Assessment recorded for {ref}.',
  'evaluator.offline.queued': 'Saved offline — will send automatically once you reconnect.',
  'evaluator.outbox.pending': '{count} pending in outbox',
  'evaluator.outbox.flushed': 'Sent {count} queued assessment(s).',

  // ── Responder (emergency-only) view ────────────────────────────────────
  'responder.title': 'Emergency lookup',
  'responder.description': 'Search a pilot to see emergency contact details only.',
  'responder.search': 'Search name, card number or reference',
  'responder.notFound.title': 'No match',
  'responder.notFound.body': 'No pilot found for that search.',
  'responder.field.name': 'Name',
  'responder.field.nationality': 'Nationality',
  'responder.field.phone': 'Pilot phone',
  'responder.field.emgContact': 'Emergency contact',
  'responder.field.emgRelationship': 'Relationship',
  'responder.field.emgPhone': 'Emergency contact phone',
  'responder.field.bloodGroup': 'Blood group',
  'responder.field.medicalValidTo': 'Medical insurance valid to',
  'responder.field.cardNo': 'Card number',
  'responder.call': 'Call',

  // ── Staff admin ────────────────────────────────────────────────────────
  'staff.title': 'Staff',
  'staff.description': 'Manage office accounts, roles and passphrases.',
  'staff.table.user': 'User',
  'staff.table.displayName': 'Display name',
  'staff.table.role': 'Role',
  'staff.table.active': 'Active',
  'staff.add': 'Add staff',
  'staff.edit': 'Edit staff',
  'staff.drawer.user': 'Username',
  'staff.drawer.displayName': 'Display name',
  'staff.drawer.role': 'Role',
  'staff.drawer.active': 'Active',
  'staff.drawer.setPassphrase': 'Set passphrase',
  'staff.drawer.passphrase1': 'New passphrase',
  'staff.drawer.passphrase2': 'Confirm passphrase',
  'staff.drawer.passphraseMismatch': "Passphrases don't match.",
  'staff.drawer.strength': 'Strength',
  'staff.drawer.strength.0': 'Very weak',
  'staff.drawer.strength.1': 'Weak',
  'staff.drawer.strength.2': 'Fair',
  'staff.drawer.strength.3': 'Strong',
  'staff.drawer.strength.4': 'Very strong',
  'staff.drawer.save': 'Save',
  'staff.saved': 'Staff record saved.',

  // ── Settings (admin) ───────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.description': 'Tenant configuration — changes take effect immediately.',
  'settings.warning': 'These settings affect the whole office. Double-check before saving.',
  'settings.config.season': 'Season',
  'settings.config.card_seq': 'Card sequence (read-only)',
  'settings.config.key_id': 'Active signing key id',
  'settings.config.ruleset_version': 'Ruleset version',
  'settings.config.thresholds': 'Thresholds',
  'settings.save': 'Save',
  'settings.saved': '{key} updated.',
  'settings.tenant.title': 'Tenant',
  'settings.demo.title': 'Demo data',
  'settings.demo.resetButton': 'Reset demo data',
  'settings.demo.resetConfirm': 'This wipes every demo application and reseeds fresh sample data. Continue?',
  'settings.demo.resetDone': 'Demo data reset.',

  // ── Exports (admin/sdm) ────────────────────────────────────────────────
  'exports.title': 'Exports',
  'exports.description': 'Download the register, audit log and card list.',
  'exports.applicationsCsv': 'Applications (CSV)',
  'exports.auditCsv': 'Audit log (CSV)',
  'exports.cardsCsv': 'Cards (CSV)',
  'exports.json': 'Full export (JSON)',
  'exports.revocations.title': 'Revocation list',
  'exports.revocations.current': 'Currently published: version {version}, {age}',
  'exports.revocations.none': 'No revocation list has been published yet.',
  'exports.revocations.draft': 'Draft: version {version}, {count} card(s)',
  'exports.revocations.signLink': 'Sign & publish in /issue',

  // ── Short workflow-state labels (chart axis/category labels — see status.* for the longer, pilot-facing sentences) ──
  'queueState.Submitted': 'Submitted',
  'queueState.Validated': 'Validated',
  'queueState.CorrectionRequested': 'Correction requested',
  'queueState.ReadyForEvaluation': 'Ready for evaluation',
  'queueState.Evaluated': 'Evaluated',
  'queueState.EvaluationFailed': 'Evaluation failed',
  'queueState.UnderReview': 'Under review',
  'queueState.Approved': 'Approved',
  'queueState.CardIssued': 'Card issued',
  'queueState.Rejected': 'Rejected',
  'queueState.Revoked': 'Revoked',
  'queueState.Expired': 'Expired',

  // ── Charts (src/charts/* — generic chrome shared by every chart) ───────
  'charts.chartView': 'Chart',
  'charts.tableView': 'Table',
  'charts.downloadCsv': 'Download CSV',
  'charts.asOf': 'As of {time}',
  'charts.noData': 'No data yet',
  'charts.other': 'Other',
  'charts.count': 'Count',
  'charts.category': 'Category',

  // ── Dashboard (SDM/DC/admin/assistant, WP9) ─────────────────────────────
  'dashboard.eyebrow': 'Dashboard',
  'dashboard.title': 'Operations dashboard',
  'dashboard.tabs.aria': 'Dashboard view',
  'dashboard.tabs.operations': 'Operations',
  'dashboard.tabs.season': 'Season',
  'dashboard.guard.signInBody': 'Dashboards are for the SDM, DC and admin roles. Sign in with your staff passphrase to continue.',
  'dashboard.guard.notAllowedBody': "Your role ({role}) doesn't have access to the dashboard.",
  'dashboard.error.title': 'Could not load the dashboard',
  'dashboard.error.body': 'The last request failed. Check your connection and try again.',
  'dashboard.error.retry': 'Retry',
  'dashboard.offline.title': 'Showing the last saved numbers',
  'dashboard.offline.body': "You're offline — this is the last snapshot saved on this device, from {time}.",
  'dashboard.asOf': 'As of {time}',
  'dashboard.refresh': 'Refresh',

  'dashboard.ops.tile.pending': 'Pending review',
  'dashboard.ops.tile.correction': 'Awaiting correction',
  'dashboard.ops.tile.correction.oldest': 'oldest {age}',
  'dashboard.ops.tile.ready': 'Ready to issue',
  'dashboard.ops.tile.issuedToday': 'Issued today',
  'dashboard.ops.tile.expiring': 'Expiring in 7 days',
  'dashboard.ops.tile.avgApproval': 'Avg approval time',
  'dashboard.ops.tile.avgApproval.value': '{hours} h',
  'dashboard.ops.tile.avgApproval.none': 'No decisions yet',
  'dashboard.ops.queueByStage.title': 'Queue by stage',
  'dashboard.ops.queueByStage.subtitle': 'Applications currently in each stage, with the oldest waiting time',
  'dashboard.ops.queueByStage.annotation': 'oldest {age}',
  'dashboard.ops.trend.title': 'Submissions and cards issued',
  'dashboard.ops.trend.subtitle': 'Last 30 days',
  'dashboard.ops.trend.series.submissions': 'Submissions',
  'dashboard.ops.trend.series.issued': 'Cards issued',
  'dashboard.ops.tierMix.title': 'Risk tier mix',
  'dashboard.ops.tierMix.subtitle': 'Findings severity across every application this season',
  'dashboard.ops.tierMix.green': 'Green',
  'dashboard.ops.tierMix.amber': 'Amber',
  'dashboard.ops.tierMix.red': 'Red',
  'dashboard.ops.evaluators.title': 'Evaluations by evaluator',
  'dashboard.ops.evaluators.subtitle': 'Pass / fail split per evaluator',
  'dashboard.ops.evaluators.series.pass': 'Pass',
  'dashboard.ops.evaluators.series.fail': 'Fail',
  'dashboard.ops.evaluators.empty': 'No evaluations recorded yet.',
  'dashboard.ops.findings.title': 'Most common findings',
  'dashboard.ops.findings.subtitle': 'Failed rule checks, ranked by frequency',
  'dashboard.ops.findings.empty': 'No failed findings recorded yet.',
  'dashboard.ops.viewInQueue': 'View in queue',

  'dashboard.season.tile.pilots': 'Pilots registered',
  'dashboard.season.tile.cardsActive': 'Cards active',
  'dashboard.season.tile.nationality': 'Indian vs foreign',
  'dashboard.season.tile.countries': 'Countries represented',
  'dashboard.season.tile.avgApproval': 'Avg approval time',
  'dashboard.season.nationality.indian': 'Indian',
  'dashboard.season.nationality.foreign': 'Foreign',
  'dashboard.season.byWeek.title': 'Registrations by week',
  'dashboard.season.byWeek.subtitle': 'Applications submitted per ISO week',
  'dashboard.season.topNationalities.title': 'Top nationalities',
  'dashboard.season.topNationalities.subtitle': 'By number of applications this season',
  'dashboard.season.byState.title': 'Applications by state',
  'dashboard.season.byState.subtitle': 'Current mix across every workflow stage',
  'dashboard.season.summary.title': 'Season summary',
  'dashboard.season.summary.text':
    'So far this season {pilots} pilots from {countries} countries have registered; {active} hold an active card; average approval in {hours} h.',
  'dashboard.season.summary.textNoApproval':
    'So far this season {pilots} pilots from {countries} countries have registered; {active} hold an active card.',
  'dashboard.season.downloadCsv': 'Download season report (CSV)',
  'dashboard.season.print': 'Print',

  // ── Public statistics page (WP9, no login, no personal data) ───────────
  'stats.eyebrow': 'Public statistics',
  'stats.hero.title': '{season} at Bir Billing',
  'stats.hero.lede': "A public, no-login view of this season's Free Flyer Card registrations — updated from the register, with no personal data.",
  'stats.hero.pilots': 'Pilots registered',
  'stats.hero.cardsActive': 'Cards active',
  'stats.hero.countries': 'Countries represented',
  'stats.byWeek.title': 'Registrations by week',
  'stats.byWeek.subtitle': 'Applications submitted per ISO week, this season',
  'stats.nationalities.title': 'Where pilots come from',
  'stats.nationalities.subtitle': 'Top 10 nationalities this season',
  'stats.nationalities.other': 'Other',
  'stats.how.title': 'How this works',
  'stats.how.body':
    'Every pilot completes an in-person, ID-checked registration at Bir Billing and receives a Free Flyer Card once the SDM office approves it.',
  'stats.how.aboutLink': 'About Parwaaz',
  'stats.how.verifyLink': 'Verify a card',
  'stats.methodology': 'Aggregate counts only, updated from the register. No personal data is shown or stored on this page.',
  'stats.asOf': 'As of {time}',
  'stats.source.live': 'Live from the register',
  'stats.source.fallback': 'Showing the last published snapshot ({time})',
  'stats.share.title': 'Share this page',
  'stats.share.copy': 'Copy link',
  'stats.share.copied': 'Link copied',
  'stats.share.share': 'Share',
  'stats.error.title': 'Could not load statistics',
  'stats.error.retry': 'Retry',

  // ── Finding rule labels (plain-language, rule id → label; used by dashboard "top findings") ──
  'findings.rule.arrival.plausible': 'Arrival date implausible',
  'findings.rule.cert.expiry': 'Certification expired',
  'findings.rule.cert.holder': 'Certificate holder mismatch',
  'findings.rule.cert.recognised': 'Certification body not recognised',
  'findings.rule.declaration.complete': 'Declaration incomplete',
  'findings.rule.dob.plausible': 'Date of birth implausible',
  'findings.rule.doc.blocklist': 'Document on blocklist',
  'findings.rule.doc.duplicate': 'Duplicate document',
  'findings.rule.doc.expiry': 'Identity document expired',
  'findings.rule.emergency.distinct': 'Emergency contact not distinct',
  'findings.rule.glider.class': 'Glider class mismatch',
  'findings.rule.glider.known': 'Glider not recognised',
  'findings.rule.ins.covers': 'Insurance cover insufficient',
  'findings.rule.ins.validity': 'Insurance not valid',
  'findings.rule.medical.validity': 'Medical fitness not valid',
  'findings.rule.mrz.checkdigits': 'MRZ check digits failed',
  'findings.rule.mrz.format': 'MRZ format invalid',
  'findings.rule.name.match': 'Name mismatch',
  'findings.rule.phone.format': 'Phone number format invalid',
  'findings.rule.reserve.present': 'Reserve parachute not declared',
  'findings.rule.visa.covers': 'Visa does not cover stay',
  'findings.rule.photo.quality': 'Photo quality issue',

  // ── Issue (WP8: /issue) ──────────────────────────────────────────────────
  'issue.eyebrow': 'Issue',
  'issue.title': 'Issue Free Flyer Cards',
  'issue.description': 'Turn an approved application into a signed, printable card, and manage the office signing key.',
  'issue.tab.key': 'Signing key',
  'issue.tab.cards': 'Issue cards',
  'issue.tab.sheet': 'Print sheet',
  'issue.tab.revocations': 'Revocations',

  'issue.key.status.title': 'Signing key status',
  'issue.key.status.description': 'Keys stored in this browser. The seed never leaves this device — see the warnings below before generating or importing one.',
  'issue.key.status.none.title': 'No signing key in this browser yet',
  'issue.key.status.none.body': 'Generate a new key, import one from a paper backup, or (in a demo build) use the demo key to get started.',
  'issue.key.badge.tenantActive': "Tenant's active key",
  'issue.key.badge.unlocked': 'Unlocked',
  'issue.key.badge.locked': 'Locked',
  'issue.key.action.lock': 'Lock',
  'issue.key.action.unlock': 'Unlock',
  'issue.key.action.exportBackup': 'Export paper backup',
  'issue.key.action.forget': 'Forget',
  'issue.key.action.generate': 'Generate new key',
  'issue.key.action.import': 'Import from paper backup',
  'issue.key.action.useDemoKey': 'Use the demo key',
  'issue.key.action.copyPublicKey': 'Copy public key',
  'issue.key.action.copySnippet': 'Copy JSON snippet',
  'issue.key.action.downloadBackup': 'Download paper backup (PDF)',
  'issue.key.backup.critical': "Required — this browser is the only copy of this key. Without the paper backup, a lost or wiped browser can never recover it and no card can be signed again.",
  'issue.key.copied': 'Copied.',
  'issue.key.error.kidRequired': 'Give this key an id.',
  'issue.key.error.tooShort': 'The passphrase must be at least 8 characters.',
  'issue.key.error.mismatch': 'The two passphrases do not match.',
  'issue.key.error.fillAll': 'Fill in every field.',
  'issue.key.field.kid': 'Key id',
  'issue.key.field.kidHint': "A short label for this key, e.g. 'office-1' — it's stored in config/tenant.json alongside the public key.",
  'issue.key.field.passphrase': 'Passphrase',
  'issue.key.field.passphraseConfirm': 'Confirm passphrase',
  'issue.key.field.publicKey': 'Public key',
  'issue.key.field.configSnippet': 'config/tenant.json snippet',
  'issue.key.field.configSnippetHint': "Paste this into the tenant's keys[] array to make this key the one cards verify against.",
  'issue.key.field.backupText': 'Backup text',
  'issue.key.field.backupTextHint': 'The 8 hex groups and 4-character checksum from the paper backup.',
  'issue.key.field.newPassphrase': 'New passphrase (for this browser)',
  'issue.key.field.confirmKid': "Type '{kid}' to confirm",
  'issue.key.generate.title': 'Generate a new signing key',
  'issue.key.generate.warning':
    'This key signs every Free Flyer Card issued from this office. It never leaves this browser — write down the paper backup and store it safely; there is no way to recover it otherwise.',
  'issue.key.generate.success': 'Key generated. Save the paper backup now — this is the only time the seed is shown.',
  'issue.key.import.title': 'Import from a paper backup',
  'issue.key.import.help': 'Paste the 8 hex groups and 4-character checksum from the printed backup, and choose a new passphrase for this browser.',
  'issue.key.import.success': 'Key imported into this browser.',
  'issue.key.unlock.title': "Unlock '{kid}'",
  'issue.key.unlock.success': 'Key unlocked for this browser tab.',
  'issue.key.demo.help': 'Imports the demo signing key into this browser under a passphrase you choose, so you can sign demo approvals.',
  'issue.key.forget.title': 'Forget this key',
  'issue.key.forget.warning': "This permanently deletes '{kid}' from this browser. If it isn't backed up elsewhere, cards can never be signed with it again on this device.",
  'issue.key.forget.success': 'Key forgotten.',
  'issue.key.export.title': "Export paper backup for '{kid}'",
  'issue.key.export.help': 'Enter the passphrase for this key to reveal it for the backup PDF.',
  'issue.key.strength.weak': 'Weak passphrase',
  'issue.key.strength.fair': 'Fair passphrase',
  'issue.key.strength.good': 'Good passphrase',
  'issue.key.strength.strong': 'Strong passphrase',

  'issue.cards.needKey.title': 'Unlock the signing key to issue cards',
  'issue.cards.needKey.body': "Unlock the tenant's active key ('{tenantKid}') on the Signing key tab first.",
  'issue.cards.needKey.wrongKey': "'{unlockedKid}' is unlocked, but the tenant's active key is '{tenantKid}'. Unlock '{tenantKid}' instead.",
  'issue.cards.empty.title': 'Nothing to issue',
  'issue.cards.empty.body': 'No approved applications are waiting for a card right now.',
  'issue.cards.count': '{count} approved application(s)',
  'issue.cards.action.issueAll': 'Issue all ({count})',
  'issue.cards.action.issue': 'Issue',
  'issue.cards.action.confirmAndSign': 'Confirm & sign',
  'issue.cards.action.printCard': 'Print this card',
  'issue.cards.batch.progress': 'Issued {done} of {total}',
  'issue.cards.batch.done': 'Batch issuance finished.',
  'issue.cards.modal.title': 'Issue card',
  'issue.cards.modal.loading': 'Allocating a card number…',
  'issue.cards.modal.confirmHelp': 'Check these details against the application before signing — this cannot be undone.',
  'issue.cards.modal.signing': 'Signing and attaching…',
  'issue.cards.field.cardNo': 'Card number',
  'issue.cards.field.validFrom': 'Valid from',
  'issue.cards.field.validTo': 'Valid to',
  'issue.cards.field.signingKey': 'Signing key',
  'issue.cards.issueSuccess': 'Card {cardNo} issued.',
  'issue.cards.addedToSheet': 'Added to print sheet',

  'issue.printSheet.description': 'Cards issued but not yet printed. Select which ones to include, then download the 8-up A4 sheet.',
  'issue.printSheet.empty.title': 'Nothing to print',
  'issue.printSheet.empty.body': 'Every issued card has already been printed.',
  'issue.printSheet.action.download': 'Download print sheet ({count})',
  'issue.printSheet.action.markPrinted': 'Mark these as printed ({count})',
  'issue.printSheet.downloaded': 'Print sheet {sheetId} downloaded.',
  'issue.printSheet.markHelp': 'Once the sheet has printed correctly, mark these cards as printed so they drop off this list.',
  'issue.printSheet.markedPrinted': '{count} card(s) marked as printed.',

  'issue.revocations.published.title': 'Currently published',
  'issue.revocations.published.none': 'No revocation list has been published yet.',
  'issue.revocations.draft.title': 'Build and publish a new list',
  'issue.revocations.draft.description': 'Drafts the current set of revoked cards from the register, for the SDM/admin to sign and publish.',
  'issue.revocations.draft.adminOnly': 'Only an admin can build a draft. Ask an admin, or sign in as one.',
  'issue.revocations.action.buildDraft': 'Build draft',
  'issue.revocations.action.signAndPublish': 'Sign & publish',
  'issue.revocations.field.version': 'Version',
  'issue.revocations.field.generatedAt': 'Generated',
  'issue.revocations.field.age': 'Age',
  'issue.revocations.field.count': 'Revoked cards',
  'issue.revocations.field.signingKey': 'Signing key',
  'issue.revocations.ageHours': '{hours} h old',
  'issue.revocations.ageMinutes': '{minutes} min old',
  'issue.revocations.needKey': "Unlock key '{keyId}' to sign this list.",
  'issue.revocations.publishSuccess': 'Revocation list published.',
  'issue.revocations.instructions.title': 'How this reaches /verify',
  'issue.revocations.instructions.body':
    'The static revocations.json on the site is refreshed by the nightly job/deploy. The /verify page also fetches the latest list live from the backend whenever it has a signal, and always falls back to the last list it downloaded when offline.',

  // ── Verify (WP8: /verify) ─────────────────────────────────────────────────
  'verify.eyebrow': '{cardName} · {site}',
  'verify.title': 'Verify a Free Flyer Card',
  'verify.description': 'Scan the QR on a printed card to check it — works even without a signal.',
  'verify.camera.error': 'Could not start the camera.',
  'verify.camera.unavailable': 'Camera not available on this device — use the options below instead.',
  'verify.clockWarning': "This device's clock looks wrong — verdicts involving dates may be unreliable.",
  'verify.list.age': 'Revocation list: {age}',
  'verify.list.loading': 'Loading the revocation list…',
  'verify.torch.on': 'Turn on torch',
  'verify.torch.off': 'Turn off torch',
  'verify.fallback.title': 'Type or paste the card QR text',
  'verify.fallback.label': 'QR text',
  'verify.fallback.hint': 'If the camera cannot scan it, type or paste the exact text printed under the QR, or found in the QR payload.',
  'verify.fallback.action': 'Check',
  'verify.lookup.title': 'Enter a card number',
  'verify.lookup.help': 'Without the QR this can only check the revocation list — it cannot confirm the card is genuine or unexpired.',
  'verify.lookup.action': 'Look up',
  'verify.lookup.revoked': 'This card number has been revoked.',
  'verify.lookup.cannotConfirm': 'Not on the revocation list — but this cannot confirm the card is genuine or unexpired without scanning its QR.',
  'verify.settings.geo': 'Record location with scans',
  'verify.settings.sound': 'Sound',
  'verify.settings.mute': 'Mute sound',
  'verify.settings.unmute': 'Unmute sound',
  'verify.pendingScans': '{count} scan(s) waiting to upload',
  'verify.install.hint': 'Install this page for offline use — it works with no signal once installed.',
  'verify.install.action': 'Install',
  'verify.offlineNote': 'This page keeps working with no signal once it has loaded.',
  'verify.badge.green': 'Valid',
  'verify.badge.amber': 'Warning',
  'verify.badge.red': 'Invalid',
  'verify.result.validTo': 'Valid to {date}',
  'verify.result.comparePhoto': 'Compare the photo on the printed card with the person in front of you.',
  'verify.result.loadPhoto': 'Load photo',
  'verify.result.scanNext': 'Scan next',

  // ── Login (staff sign-in) ─────────────────────────────────────────────────
  'login.eyebrow': 'Staff',
  'login.title': 'Sign in',
  'login.description': 'For Office of the SDM Baijnath staff — pilots do not need an account.',
  'login.field.user': 'User',
  'login.field.passphrase': 'Passphrase',
  'login.field.showPassphrase': 'Show',
  'login.field.hidePassphrase': 'Hide',
  'login.field.showPassphraseAria': 'Show passphrase',
  'login.field.hidePassphraseAria': 'Hide passphrase',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.signedInAs': 'Signed in as {name}',
  'login.error.generic': 'Could not sign in. Check your connection and try again.',
  'login.demo.title': 'Demo accounts',
  'login.demo.badge': 'demo build',
  'login.demo.intro': 'This build runs entirely in your browser with sample data. Sign in with any of these, using the passphrase {passphrase}:',

  // ── Home & About (marketing) ────────────────────────────────────────────────
  'home.eyebrow': '{office} · {district} District',
  'home.steps.register.title': 'Register online',
  'home.steps.register.body': 'Fill in your details from your phone, anywhere.',
  'home.steps.checked.title': 'Documents checked instantly',
  'home.steps.checked.body': 'Mistakes are caught before you submit.',
  'home.steps.evaluation.title': 'Evaluation & SDM approval',
  'home.steps.evaluation.body': 'Our team and the SDM confirm you can fly.',
  'home.steps.card.title': 'Your {cardName}',
  'home.steps.card.body': 'A signed QR you can show — even with no signal.',
  'home.needed.id': 'Passport / photo ID',
  'home.needed.photo': 'A passport-style photo',
  'home.needed.cert': 'Paragliding certification',
  'home.needed.insurance': 'Insurance covering paragliding',
  'home.needed.glider': 'Glider details',
  'home.needed.emergency': 'Emergency contact',
  'home.season.cta': 'Register for this season →',
  'home.trust.offline.title': 'Offline-verifiable',
  'home.trust.offline.body': 'Every card carries a signed QR that checks out with no signal at all.',
  'home.trust.dataMin.title': 'Data minimised',
  'home.trust.dataMin.body': "We collect only what's needed to register you and issue your card.",
  'home.trust.dpdp.title': 'DPDP notice',
  'home.trust.dpdp.link': 'Read our privacy notice →',
  'home.statusCheck.title': 'Check my status',
  'home.statusCheck.intro': 'Use the reference and token from your registration confirmation — or paste the status link we sent you.',
  'home.statusCheck.linkLabel': 'Status link',
  'home.statusCheck.linkHint': "Paste the full link — we'll fill in the fields below",
  'home.statusCheck.refLabel': 'Reference number',
  'home.statusCheck.tokenLabel': 'Access token',
  'home.statusCheck.submit': 'View status',
  'home.heroArt.alt': 'Paragliders soaring above the Dhauladhar ridge at Bir Billing',

  'about.eyebrow': '{district} district · {state}',
  'about.title': 'About {name}',
  'about.description': '{name} is the digital {cardName} registry for visiting paraglider pilots at {site}, built for {authority}.',
  'about.howItWorks': 'How it works',
  'about.steps.register.title': 'Register online',
  'about.steps.register.body': 'Fill in your details, document, certification, glider and insurance from your phone — no office visit needed to start.',
  'about.steps.checked.title': 'Documents checked instantly',
  'about.steps.checked.body': 'Your document number, dates and certification are checked as you type, so mistakes are caught before you submit.',
  'about.steps.evaluation.title': 'Evaluation & SDM approval',
  'about.steps.evaluation.body': 'Our team confirms your certification and glider class against the season rules; the SDM approves the application.',
  'about.steps.card.title': 'Your {cardName}',
  'about.steps.card.body': 'You get a card with a signed QR code that can be verified offline at the launch site, on paper or on your phone.',
  'about.whatYouNeed': "What you'll need",
  'about.needed.id': 'Passport or other photo ID',
  'about.needed.photo': 'A passport-style photograph',
  'about.needed.cert': 'Your paragliding certification',
  'about.needed.insurance': 'Insurance covering paragliding',
  'about.needed.glider': 'Your glider make, model and class',
  'about.needed.emergency': 'An emergency contact',
  'about.yourData': 'Your data',
  'about.privacy.title': 'Data minimised, under the Digital Personal Data Protection Act',
  'about.privacy.body1':
    "We collect only what's needed to check your eligibility to fly and to issue your card: your identity document, certification, glider and insurance details, and an emergency contact. Your document is used to verify your identity and is not published; your card shows only your name, nationality, certification and a card number.",
  'about.privacy.body2':
    'Data is retained for {years} years as required for regulatory and safety purposes, and you may write to the grievance officer below to ask what we hold about you or to request a correction.',
  'about.contact': 'Contact',
  'about.grievanceOfficer': 'Grievance officer: {officer}',
  'about.builtFor': 'Built for {authority}.',
};
